/**
 * PRESENCE CONTEXT
 *
 * Single source of truth for "who is online", "who is typing", and
 * "last seen" timestamps. The existing Socket.io / WebSocket connection
 * in src/App.js is reused — this context just exposes a clean API on
 * top of the raw socket messages so every page (messages, freelancer
 * cards, profile headers) can subscribe without touching the WS code.
 *
 * The context never *creates* a socket. It holds a ref to the live
 * WebSocket (set once by App.js) and exposes helpers that send presence
 * events when available. If the socket is missing (logged-out / offline),
 * the helpers degrade gracefully — presence reads become no-ops and
 * `isOnline(userId)` just returns false.
 *
 * Backend contract (tolerant — any of these event shapes work):
 *   { type: "presence", user_id, status: "online"|"away"|"offline", last_seen }
 *   { type: "presence_bulk", users: [ { user_id, status, last_seen } ] }
 *   { type: "typing", user_id, conversation_id, is_typing }
 */
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

const PresenceContext = createContext({
  isOnline: () => false,
  getStatus: () => 'offline',
  getLastSeen: () => null,
  isTyping: () => false,
  setSocket: () => {},
  handleSocketMessage: () => {},
  announceTyping: () => {},
});

const TYPING_TIMEOUT_MS = 3500;

export const PresenceProvider = ({ children }) => {
  // user_id -> { status, lastSeen }
  const [presence, setPresence] = useState({});
  // conversation_id -> Set<user_id>
  const [typing, setTyping] = useState({});

  const socketRef = useRef(null);
  const typingTimersRef = useRef(new Map());

  const setSocket = useCallback((ws) => {
    socketRef.current = ws || null;
  }, []);

  const clearTypingTimer = useCallback((key) => {
    const t = typingTimersRef.current.get(key);
    if (t) {
      clearTimeout(t);
      typingTimersRef.current.delete(key);
    }
  }, []);

  const markTyping = useCallback(
    (conversationId, userId, isTyping) => {
      if (!conversationId || !userId) return;
      const key = `${conversationId}:${userId}`;
      clearTypingTimer(key);

      setTyping((prev) => {
        const current = prev[conversationId]
          ? new Set(prev[conversationId])
          : new Set();
        if (isTyping) current.add(userId);
        else current.delete(userId);
        return { ...prev, [conversationId]: current };
      });

      if (isTyping) {
        // Auto-expire typing indicator if no follow-up stop event arrives.
        const timer = setTimeout(() => {
          setTyping((prev) => {
            const current = prev[conversationId]
              ? new Set(prev[conversationId])
              : new Set();
            current.delete(userId);
            return { ...prev, [conversationId]: current };
          });
          typingTimersRef.current.delete(key);
        }, TYPING_TIMEOUT_MS);
        typingTimersRef.current.set(key, timer);
      }
    },
    [clearTypingTimer]
  );

  const applyPresence = useCallback((userId, status, lastSeen) => {
    if (!userId) return;
    setPresence((prev) => ({
      ...prev,
      [String(userId)]: {
        status: status || 'offline',
        lastSeen: lastSeen || prev[String(userId)]?.lastSeen || null,
      },
    }));
  }, []);

  /**
   * Feed any incoming socket message through this handler. It's a no-op
   * for unrelated events, so callers can pipe every message through it.
   */
  const handleSocketMessage = useCallback(
    (raw) => {
      if (!raw) return;
      let msg = raw;
      if (typeof raw === 'string') {
        try {
          msg = JSON.parse(raw);
        } catch {
          return;
        }
      }
      if (!msg || typeof msg !== 'object') return;

      switch (msg.type) {
        case 'presence':
          applyPresence(msg.user_id, msg.status, msg.last_seen);
          break;
        case 'presence_bulk':
          (msg.users || []).forEach((u) =>
            applyPresence(u.user_id, u.status, u.last_seen)
          );
          break;
        case 'typing':
          markTyping(
            msg.conversation_id,
            msg.user_id,
            msg.is_typing !== false
          );
          break;
        case 'typing_stop':
          markTyping(msg.conversation_id, msg.user_id, false);
          break;
        default:
          break;
      }
    },
    [applyPresence, markTyping]
  );

  /**
   * Broadcast that the current user is typing. Safe to call on every
   * keypress — callers should still debounce for efficiency.
   */
  const announceTyping = useCallback((conversationId, isTyping = true) => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== 1 /* OPEN */) return;
    try {
      ws.send(
        JSON.stringify({
          type: isTyping ? 'typing' : 'typing_stop',
          conversation_id: conversationId,
          is_typing: isTyping,
        })
      );
    } catch {
      // swallow — presence is best-effort
    }
  }, []);

  const isOnline = useCallback(
    (userId) => presence[String(userId)]?.status === 'online',
    [presence]
  );
  const getStatus = useCallback(
    (userId) => presence[String(userId)]?.status || 'offline',
    [presence]
  );
  const getLastSeen = useCallback(
    (userId) => presence[String(userId)]?.lastSeen || null,
    [presence]
  );
  const isTypingFn = useCallback(
    (conversationId, excludeUserId) => {
      const set = typing[conversationId];
      if (!set || set.size === 0) return false;
      if (!excludeUserId) return true;
      // Are any users other than me typing?
      for (const uid of set) {
        if (String(uid) !== String(excludeUserId)) return true;
      }
      return false;
    },
    [typing]
  );
  const typingUsers = useCallback(
    (conversationId, excludeUserId) => {
      const set = typing[conversationId];
      if (!set) return [];
      return [...set].filter(
        (uid) => !excludeUserId || String(uid) !== String(excludeUserId)
      );
    },
    [typing]
  );

  useEffect(() => {
    return () => {
      typingTimersRef.current.forEach((t) => clearTimeout(t));
      typingTimersRef.current.clear();
    };
  }, []);

  const value = useMemo(
    () => ({
      isOnline,
      getStatus,
      getLastSeen,
      isTyping: isTypingFn,
      typingUsers,
      setSocket,
      handleSocketMessage,
      announceTyping,
    }),
    [
      isOnline,
      getStatus,
      getLastSeen,
      isTypingFn,
      typingUsers,
      setSocket,
      handleSocketMessage,
      announceTyping,
    ]
  );

  return (
    <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
  );
};

export const usePresence = () => useContext(PresenceContext);

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

/**
 * Small colored dot indicating a user's live status. Intended to sit on
 * top of avatars on freelancer cards, conversation lists, etc.
 */
export const PresenceDot = ({ userId, className = '', size = 'sm' }) => {
  const { getStatus } = usePresence();
  const status = getStatus(userId);
  const sizes = { xs: 'w-2 h-2', sm: 'w-2.5 h-2.5', md: 'w-3 h-3' };
  const colors = {
    online: 'bg-emerald-500',
    away: 'bg-amber-400',
    offline: 'bg-slate-300',
  };
  return (
    <span
      aria-label={`Status: ${status}`}
      className={`inline-block rounded-full ring-2 ring-white ${sizes[size] || sizes.sm} ${colors[status] || colors.offline} ${className}`}
    />
  );
};

/**
 * Plain-text label: "Active now" / "Last seen 2h ago" / "Offline".
 * Uses Intl.RelativeTimeFormat so it respects the user's locale.
 */
export const formatLastSeen = (iso, locale) => {
  if (!iso) return 'Offline';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Offline';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'Last seen just now';
  const rtf = new Intl.RelativeTimeFormat(locale || undefined, {
    numeric: 'auto',
  });
  const units = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSec) >= seconds) {
      const value = Math.round(diffSec / seconds);
      return `Last seen ${rtf.format(-value, unit)}`;
    }
  }
  return 'Last seen just now';
};

export const PresenceLabel = ({ userId, className = '' }) => {
  const { getStatus, getLastSeen } = usePresence();
  const status = getStatus(userId);
  const lastSeen = getLastSeen(userId);
  if (status === 'online') {
    return (
      <span className={`inline-flex items-center gap-1.5 text-emerald-600 text-xs font-medium ${className}`}>
        <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full" />
        Active now
      </span>
    );
  }
  return (
    <span className={`text-xs text-slate-500 ${className}`}>
      {formatLastSeen(lastSeen)}
    </span>
  );
};

/**
 * Renders "Alice is typing..." / "Alice and Bob are typing..." for a
 * given conversation, excluding the current user.
 */
export const TypingIndicator = ({
  conversationId,
  excludeUserId,
  nameLookup,
  className = '',
}) => {
  const { typingUsers } = usePresence();
  const users = typingUsers(conversationId, excludeUserId);
  if (users.length === 0) return null;

  const names = users
    .map((uid) =>
      typeof nameLookup === 'function' ? nameLookup(uid) : null
    )
    .filter(Boolean);

  let label;
  if (names.length === 0) {
    label = users.length === 1 ? 'Someone is typing…' : 'People are typing…';
  } else if (names.length === 1) {
    label = `${names[0]} is typing…`;
  } else if (names.length === 2) {
    label = `${names[0]} and ${names[1]} are typing…`;
  } else {
    label = `${names[0]}, ${names[1]} and ${names.length - 2} more are typing…`;
  }

  return (
    <div
      aria-live="polite"
      className={`flex items-center gap-2 text-xs text-slate-500 ${className}`}
    >
      <span className="flex gap-0.5" aria-hidden="true">
        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
        <span
          className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
          style={{ animationDelay: '120ms' }}
        />
        <span
          className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
          style={{ animationDelay: '240ms' }}
        />
      </span>
      <span>{label}</span>
    </div>
  );
};

export default PresenceContext;
